import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { NgClass } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Breadcrumb, PageTitle, PageTitleComponent } from 'app/layout/common/page-title/page-title.component';
import { SearchBarComponent } from 'app/common/components/search-bar/search-bar.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { fuseAnimations } from '@fuse/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { startWith } from 'rxjs';
import { DashboardCardComponent } from 'app/modules/dashboard/dashboard-card/dashboard-card.component';
import { MatTabNav, MatTabsModule } from "@angular/material/tabs";
import { FormControl } from '@angular/forms';
import { DashboardAppService } from '../dashboard.app.service';
import { AuthAppService } from 'app/modules/auth/auth.app.service';
import { DashboardListDto, SectorListDto, ThematicListDto } from 'app/common/generated';
import { SectorAppService } from 'app/modules/sector';
import { ThematicAppService } from 'app/modules/thematic';
import { DashboardShareAppService } from 'app/modules/dashboard-share';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { DEFAULT_MODE, STORAGE_MODE_KEY } from 'app/common';

@Component({
    selector: 'app-dashboard-list',
    imports: [ReactiveFormsModule, TranslocoModule, RouterLink, MatTooltipModule, MatDividerModule,
        MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatMenuModule, DashboardCardComponent, MatTabsModule, MatTabNav, PageTitleComponent, SearchBarComponent],
    templateUrl: './dashboard-list.component.html',
    styleUrls: ['./dashboard-list.component.scss'],
    animations: fuseAnimations
})
export class DashboardListComponent implements OnInit {
    private readonly dashboardService = inject(DashboardAppService);
    private readonly dashboardShareService = inject(DashboardShareAppService);
    private readonly sectorService = inject(SectorAppService);
    private readonly thematicService = inject(ThematicAppService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly authService = inject(AuthAppService);

    private static readonly STORAGE_KEY = 'dashboardList.selectedSectorId';

    title: PageTitle = { title: 'dashboard-list.title', description: 'dashboard-list.description' };
    breadcrumbs: Breadcrumb[] = [
        { label: 'home.title', url: '/home', icon: 'heroicons_mini:home' },
        { label: 'dashboard-list.title' }
    ];

    dashboards = signal<DashboardListDto[]>([]);
    preferredDashboards = toSignal(this.dashboardService.getPreferred(), { initialValue: [] });
    sectors = toSignal(this.sectorService.getTopSectors(), { initialValue: [] });

    selectedSectorId = signal<string | null>(null);
    subSectors = signal<SectorListDto[]>([]);
    thematics = signal<ThematicListDto[]>([]);
    activeSubSector = signal<SectorListDto | null>(null);
    activeThematic = signal<ThematicListDto | null>(null);

    hasActiveFilters = computed(() => !!this.activeSubSector() || !!this.activeThematic());

    isLoading = signal(true);
    isAdmin = toSignal(this.authService.hasRole('Admin'), { initialValue: false });

    searchControl: FormControl = new FormControl('');
    searchTerm = toSignal(
        this.searchControl.valueChanges.pipe(startWith('')),
        { initialValue: '' }
    );

    mode = signal<'group' | 'grid'>(
        (localStorage.getItem(STORAGE_MODE_KEY) as 'group' | 'grid') ?? DEFAULT_MODE
    );

    groupedDashboards = computed(() => {
        const dashboards = this.dashboardToDisplay();

        const groupMap = new Map<string, {
            subSectorId: string | null;
            thematicId: string | null;
            subSectorName: string | null;
            thematicName: string | null;
            dashboards: DashboardListDto[];
        }>();

        for (const dashboard of dashboards) {
            const key = `${dashboard.subSectorId ?? ''}|${dashboard.thematicId ?? ''}`;
            if (!groupMap.has(key)) {
                groupMap.set(key, {
                    subSectorId: dashboard.subSectorId ?? null,
                    thematicId: dashboard.thematicId ?? null,
                    subSectorName: dashboard.subSectorName ?? null,
                    thematicName: dashboard.thematicName ?? null,
                    dashboards: []
                });
            }
            groupMap.get(key)!.dashboards.push(dashboard);
        }

        return Array.from(groupMap.values());
    });

    // Computed signal pour filtrer les dashboards en fonction du secteur sélectionné et de la recherche
    dashboardToDisplay = computed(() => {
        const sectorId = this.selectedSectorId();
        const allDashboards = this.dashboards();
        const search = this.searchTerm()?.toLowerCase().trim() || '';
        const subSector = this.activeSubSector();
        const thematic = this.activeThematic();

        // Filtrer par secteur
        let filtered = sectorId
            ? allDashboards.filter(dashboard => String(dashboard.sectorId) === sectorId)
            : allDashboards;

        // Filtrer par sous-secteur
        if (subSector) {
            filtered = filtered.filter(dashboard => dashboard.subSectorId === subSector.id);
        }

        // Filtrer par thématique
        if (thematic) {
            filtered = filtered.filter(dashboard => dashboard.thematicId === thematic.id);
        }

        // Filtrer par recherche (nom, description, secteur, sous-secteur, thématique)
        if (search) {
            filtered = filtered.filter(dashboard => {
                const name = dashboard.name?.toLowerCase() || '';
                const description = dashboard.description?.toLowerCase() || '';
                const sectorName = dashboard.sectorName?.toLowerCase() || '';
                const subSectorName = dashboard.subSectorName?.toLowerCase() || '';
                const thematicName = dashboard.thematicName?.toLowerCase() || '';

                return name.includes(search) ||
                    description.includes(search) ||
                    sectorName.includes(search) ||
                    subSectorName.includes(search) ||
                    thematicName.includes(search);
            });
        }

        return filtered;
    });

    refreshTick = signal(0);
    refreshDashboards(): void { this.refreshTick.update(v => v + 1); }

    constructor() {
        effect(() => {
            this.refreshTick();
            const sectorId = this.selectedSectorId();
            if (sectorId) {
                this.dashboardService.getBySector(sectorId).subscribe(dashboards => {
                    this.dashboards.set(dashboards);
                });
            } else {
                this.dashboardService.getAll().subscribe(dashboards => {
                    this.dashboards.set(dashboards);
                });
            }
            this.isLoading.set(false);
        });
        effect(() => {
            const sectorId = this.selectedSectorId();
            if (sectorId) {
                this.sectorService.getSubSectors(sectorId).subscribe({
                    next: (subSectors) => {
                        this.subSectors.set(subSectors);
                        this.activeSubSector.set(null);
                        this.activeThematic.set(null);
                    }
                });
            } else {
                this.subSectors.set([]);
                this.activeSubSector.set(null);
                this.activeThematic.set(null);
            }
        });
        effect(() => {
            const subSector = this.activeSubSector();
            if (subSector?.id) {
                this.thematicService.getThematicsBySectorId(subSector.id).subscribe({
                    next: (thematics) => {
                        this.thematics.set(thematics);
                        this.activeThematic.set(null);
                    }
                });
            } else {
                this.thematics.set([]);
                this.activeThematic.set(null);
            }
        });
    }

    ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            const sectorId = params['sectorId'];
            if (sectorId) {
                this.selectedSectorId.set(sectorId);
                localStorage.setItem(DashboardListComponent.STORAGE_KEY, sectorId);
            }
            else {
                const savedSectorId = localStorage.getItem(DashboardListComponent.STORAGE_KEY);
                if (savedSectorId) {
                    this.selectedSectorId.set(savedSectorId);
                } else {
                    this.selectedSectorId.set(null);
                }
            }
        });
    }

    // Actions filtres
    applySubSectorFilter(subSector: SectorListDto): void {
        this.activeSubSector.set(subSector);
    }

    applyThematicFilter(thematic: ThematicListDto): void {
        this.activeThematic.set(thematic);
    }

    clearSubSectorFilter(): void {
        this.activeSubSector.set(null);
        this.clearThematicFilter();
    }

    clearThematicFilter(): void {
        this.activeThematic.set(null);
    }

    clearAllFilters(): void {
        this.clearSubSectorFilter();
    }

    toggleMode(): void {
        const next = this.mode() === 'grid' ? 'group' : 'grid';
        this.mode.set(next);
        localStorage.setItem(STORAGE_MODE_KEY, next);
    }

    onCreateDashboard(): void {
        this.router.navigate(['/data-management/dashboard-builder']);
    }

    onViewDashboard(dashboard: DashboardListDto): void {
        this.router.navigate(['/data-management/dashboards', dashboard.id]);
    }

    onEditDashboard(dashboard: DashboardListDto): void {
        this.dashboardService.updateInfo(dashboard.id, dashboard.name, dashboard.description, dashboard.thematicId).subscribe(() => {
            this.refreshDashboards();
        });
    }

    /**
     * Éditer le design du dashboard (graphics, layout, etc.)
     */
    onEditDesign(dashboard: DashboardListDto): void {
        this.router.navigate(['/data-management/dashboard-builder', dashboard.id]);
    }

    onDeleteDashboard(dashboard: DashboardListDto): void {
        this.dashboardService.delete(dashboard.id).subscribe(success => {
            if (success) {
                // Rafraîchir la liste des dashboards après suppression
                this.dashboards.update(current => current.filter(d => d.id !== dashboard.id));
            }
        });
    }

    onTogglePreferred(dashboard: DashboardListDto): void {
        this.dashboardService.setPreferred(dashboard.id, !this.isPreferred(dashboard)).subscribe({
            next: () => {
                this.preferredDashboards = toSignal(this.dashboardService.getPreferred(), { initialValue: [] });
            }
        })
    }

    /**
     * Vérifie si un dashboard est dans la liste des préférés
     */
    isPreferred(dashboard: DashboardListDto): boolean {
        return this.preferredDashboards().some(preferred => preferred.id === dashboard.id);
    }

    getStatusClass(isActive: boolean): string {
        return isActive ? 'text-green-500' : 'text-gray-400';
    }

    getVisibilityIcon(isPublic: boolean): string {
        return isPublic ? 'heroicons_outline:globe-alt' : 'heroicons_outline:lock-closed';
    }

    getVisibilityText(isPublic: boolean): string {
        return isPublic ? 'dashboard-list.public' : 'dashboard-list.private';
    }

    openShareDialog(dashboardId: string): void {
        this.dashboardShareService.openShareDialog(dashboardId).subscribe(result => {
            if (result) {
                this.dashboardShareService.createDashboardShare(result).subscribe(() => {
                    // Optionally show a success message or refresh the shares list
                });
            }
        });
    }
}
